/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorBlock } from '../../components/ConsultaSeguimentoIndicatorBlock'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { ExemplosVerticalField, ExemplosVerticalFieldOptions } from '../../components/ExemplosVerticalField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta10ExemplosEnum,
  ConsultaSeguimentoPergunta10Model,
} from './model-consultaSeguimentoPergunta10'
import {
  hasPergunta10ExemplosFalhouAnySim,
  hasPergunta10ExemplosPassouAnySim,
  isPergunta10ExemplosAllFilled,
  isPergunta10ExemplosAllNao,
} from './utils-consultaSeguimentoPergunta10'

interface ConsultaSeguimentoPergunta10FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta10Model>
}

export function ConsultaSeguimentoPergunta10Field(props: Readonly<ConsultaSeguimentoPergunta10FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta10Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta10Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_10_HEADER === SimNaoEnum.SIM
  const isPerguntaExemplosAllNao = isPergunta10ExemplosAllNao(value?.PERGUNTA_10_EXEMPLOS)
  const hasPerguntaExemplosPassouAnySim = hasPergunta10ExemplosPassouAnySim(value?.PERGUNTA_10_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta10ExemplosFalhouAnySim(value?.PERGUNTA_10_EXEMPLOS)
  const hasSimOnlyInExemplosPassou = hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim
  const hasSimOnlyInExemplosFalhou = hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim

  const textResultadoFalhouWithAnySim = hasSimOnlyInExemplosFalhou
    ? 'Sim somente para exemplos Falhou:'
    : 'Os mais frequentes são exemplos de Falhou:'
  const textResultadoFalhou = isPerguntaExemplosAllNao ? 'Não para todos' : textResultadoFalhouWithAnySim
  const textResultadoPassou = hasSimOnlyInExemplosPassou
    ? 'Sim somente para exemplos Passou:'
    : 'Os mais frequentes são exemplos de Passou:'

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_10}
          title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' responde quando você o chama pelo nome?' />}
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_10_HEADER} />}
        />
        {!isEmpty(value.PERGUNTA_10_HEADER) && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              <ConsultaSeguimentoIndicatorHelper
                title={
                  isPerguntaHeaderSim
                    ? 'Por favor, dê um exemplo de como ele responde quando você o chama pelo nome.'
                    : 'Se ele não estiver ocupado com alguma coisa divertida ou interessante, o que ele faz quando você o chama pelo nome?'
                }
                subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
              />
            </VFlow>
            <VFlow vSpacing={0}>
              <ExemplosVerticalField<ConsultaSeguimentoPergunta10ExemplosEnum>
                name={name.PERGUNTA_10_EXEMPLOS}
                label='Responda todos os exemplos, conforme as respostas do genitor'
                options={PERGUNTA_10_EXEMPLOS_OPTIONS}
                title='Ele...'
                required
              />
              {isPerguntaExemplosAllNao && <ConsultaSeguimentoIndicatorBlock />}
              {hasPerguntaExemplosPassouAnySim &&
                hasPerguntaExemplosFalhouAnySim &&
                isPergunta10ExemplosAllFilled(value.PERGUNTA_10_EXEMPLOS) && (
                  <VFlow vSpacing={0}>
                    <ConsultaSeguimentoIndicatorStep
                      text={
                        <VFlow vSpacing={0}>
                          <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                          <Heading level={3} color='inherit'>
                            Pergunta sobre a frequência
                          </Heading>
                        </VFlow>
                      }
                      style={styles.indicatorStep}
                    />
                    <RadioGroupBoxField
                      name={name.PERGUNTA_10_FREQUENCIA}
                      label='Quais os que ele faz com mais frequência?'
                      options={PERGUNTA_10_FREQUENCIA_OPTIONS}
                      required
                    />
                  </VFlow>
                )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: textResultadoFalhou,
            PASSOU: textResultadoPassou,
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta10Model): boolean => {
  if (
    !isPergunta10ExemplosAllFilled(values.PERGUNTA_10_EXEMPLOS) ||
    isPergunta10ExemplosAllNao(values?.PERGUNTA_10_EXEMPLOS)
  ) {
    return false
  }

  const hasPerguntaExemplosPassouAnySim = hasPergunta10ExemplosPassouAnySim(values.PERGUNTA_10_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta10ExemplosFalhouAnySim(values.PERGUNTA_10_EXEMPLOS)

  if (
    (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) ||
    (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim)
  ) {
    return true
  } else {
    return !isEmpty(values.PERGUNTA_10_FREQUENCIA)
  }
}

const calculateResultado = (values: ConsultaSeguimentoPergunta10Model): PassouFalhouEnum => {
  const hasPerguntaExemplosPassouAnySim = hasPergunta10ExemplosPassouAnySim(values.PERGUNTA_10_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta10ExemplosFalhouAnySim(values.PERGUNTA_10_EXEMPLOS)

  if (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) {
    return PassouFalhouEnum.PASSOU
  } else if (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim) {
    return PassouFalhouEnum.FALHOU
  } else {
    return values.PERGUNTA_10_FREQUENCIA
  }
}

const PERGUNTA_10_EXEMPLOS_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta10ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Procura?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.PROCURA,
    },
    {
      label: 'Fala ou emite algum som?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.FALA_OU_EMITE_SOM,
    },
    {
      label: 'Para o que está fazendo?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.PARA_O_QUE_ESTA_FAZENDO,
    },
  ],
  FALHOU: [
    {
      label: 'Não dá nenhuma resposta?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.NAO_DA_RESPOSTA,
    },
    {
      label: 'Parece ouvir mas ignora a pessoa?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.PARECE_OUVIR_IGNORA,
    },
    {
      label: 'Só responde se a pessoa estiver bem na sua frente?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.SO_RESPONDE_NA_FRENTE,
    },
    {
      label: 'Só responde se for tocado?',
      value: ConsultaSeguimentoPergunta10ExemplosEnum.SO_RESPONDE_SE_TOCADO,
    },
  ],
}

const PERGUNTA_10_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
}
